import React, { useRef, useCallback, useEffect, useState, useImperativeHandle, forwardRef } from 'react';

// Scribble canvas — a teaching/note-taking layer that floats above the grid.
//
// Tools:
//   • Pen      — current `color` from props, line width 2.5 (25% heavier than legacy 2px)
//   • Eraser   — toggled via prop `eraserActive`; uses globalCompositeOperation
//                 'destination-out' so it removes only scribble pixels and never
//                 touches the grid below.
// Stroke history supports independent undo/redo of pen strokes AND erase strokes.

const PEN_LINE_WIDTH = 3.25;      // bumped — heavier marker line per user request
const ERASER_LINE_WIDTH = 26;     // bigger eraser diameter for easier targeting

export const ScribbleCanvas = React.memo(forwardRef(function ScribbleCanvas(
  { active, onClear, color = '#0094FF', eraserActive = false },
  ref,
) {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPoint = useRef(null);
  const [hasStrokes, setHasStrokes] = useState(false);

  // Stroke history. Each stroke records its tool (pen|eraser), color, width,
  // and the point list so we can replay the history deterministically.
  const strokesRef = useRef([]);
  const currentStrokeRef = useRef(null);
  const redoStackRef = useRef([]);

  const getPoint = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: (touch.clientX - rect.left) * (canvas.width / rect.width),
      y: (touch.clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  const drawStrokeOnCtx = useCallback((ctx, stroke) => {
    if (!stroke || stroke.points.length < 2) return;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
    for (let i = 1; i < stroke.points.length; i++) {
      ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
    }
    if (stroke.tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = stroke.width;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.width;
    }
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.restore();
  }, []);

  const redrawAll = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    strokesRef.current.forEach(s => drawStrokeOnCtx(ctx, s));
    setHasStrokes(strokesRef.current.length > 0);
  }, [drawStrokeOnCtx]);

  const startDraw = useCallback((e) => {
    if (!active) return;
    e.preventDefault();
    isDrawing.current = true;
    const point = getPoint(e);
    lastPoint.current = point;
    currentStrokeRef.current = {
      tool: eraserActive ? 'eraser' : 'pen',
      color,
      width: eraserActive ? ERASER_LINE_WIDTH : PEN_LINE_WIDTH,
      points: point ? [point] : [],
    };
  }, [active, getPoint, eraserActive, color]);

  const draw = useCallback((e) => {
    if (!isDrawing.current || !active) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !lastPoint.current || !currentStrokeRef.current) return;

    const point = getPoint(e);
    if (!point) return;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(point.x, point.y);
    if (currentStrokeRef.current.tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = ERASER_LINE_WIDTH;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = color;
      ctx.lineWidth = PEN_LINE_WIDTH;
    }
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.restore();

    lastPoint.current = point;
    currentStrokeRef.current.points.push(point);
    setHasStrokes(true);
  }, [active, getPoint, color]);

  const endDraw = useCallback(() => {
    if (isDrawing.current && currentStrokeRef.current && currentStrokeRef.current.points.length > 1) {
      strokesRef.current.push(currentStrokeRef.current);
      redoStackRef.current = []; // new stroke clears redo
    }
    isDrawing.current = false;
    lastPoint.current = null;
    currentStrokeRef.current = null;
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      strokesRef.current = [];
      redoStackRef.current = [];
      setHasStrokes(false);
    }
  }, []);

  const undoStroke = useCallback(() => {
    if (strokesRef.current.length === 0) return false;
    const last = strokesRef.current.pop();
    redoStackRef.current.push(last);
    redrawAll();
    return true;
  }, [redrawAll]);

  const redoStroke = useCallback(() => {
    if (redoStackRef.current.length === 0) return false;
    const next = redoStackRef.current.pop();
    strokesRef.current.push(next);
    redrawAll();
    return true;
  }, [redrawAll]);

  // Expose imperative API to parent.
  useImperativeHandle(ref, () => ({
    clear: clearCanvas,
    undo: undoStroke,
    redo: redoStroke,
    canUndo: () => strokesRef.current.length > 0,
    canRedo: () => redoStackRef.current.length > 0,
    hasStrokes: () => strokesRef.current.length > 0,
  }), [clearCanvas, undoStroke, redoStroke]);

  // Legacy onClear support
  useEffect(() => {
    if (onClear) onClear.current = clearCanvas;
  }, [onClear, clearCanvas]);

  // Resize canvas to match container.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;

    const resize = () => {
      const rect = parent.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const nextW = Math.round(rect.width * 2);
      const nextH = Math.round(rect.height * 2);
      if (canvas.width !== nextW || canvas.height !== nextH) {
        canvas.width = nextW;
        canvas.height = nextH;
      }
      redrawAll();
    };
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(parent);
    window.addEventListener('resize', resize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, [redrawAll]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-20"
      style={{
        touchAction: active ? 'none' : 'auto',
        cursor: active ? (eraserActive ? 'cell' : 'crosshair') : 'default',
        pointerEvents: active ? 'auto' : 'none',
        width: '100%',
        height: '100%',
      }}
      onMouseDown={startDraw}
      onMouseMove={draw}
      onMouseUp={endDraw}
      onMouseLeave={endDraw}
      onTouchStart={startDraw}
      onTouchMove={draw}
      onTouchEnd={endDraw}
      data-testid="scribble-canvas"
    />
  );
}));
